import React, { useCallback, useEffect, useState } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { AnySchema } from 'yup';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { SxStyle } from '../../../types';
import { Option } from '../../../types';
import MenuItem from '@mui/material/MenuItem';
import { InputWithValidateProps } from './text-input-with-validate';
import Checkbox from '@mui/material/Checkbox';
import { useFieldValidation } from '../../../hooks/formValidate';

const styles: SxStyle = {
  formControl: {
    minWidth: 200,
  },
};

export type SelectWithValidateProps<T> = SelectProps &
  InputWithValidateProps & {
    validation?: AnySchema;
    value?: Option[];
    name: keyof T & string;
    options: Option[];
    optionsText: string[];
    onValue?: (key: string, value: Option[]) => void;
    checkboxes?: boolean;
  };

function MultipleSelectWithValidate<T>({
  value,
  name,
  label,
  onValue,
  onValidation,
  validation,
  fullWidth,
  hideValidationError,
  options,
  optionsText,
  checkboxes,
  ...props
}: SelectWithValidateProps<T>) {
  const { strings } = useStringsContext();
  const { doValidate, errorMessage } = useFieldValidation();
  const [controlledValue, setControlledValue] = useState<Option[]>(value ?? []);

  useEffect(() => {
    setControlledValue(value ?? []);
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(controlledValue, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [controlledValue, doValidate, name, onValidation, validation]);

  const onChangeEvent = useCallback(
    (event: SelectChangeEvent<Option[]>) => {
      const { value } = event.target;
      setControlledValue(value as Option[]);
      if (onValue) {
        onValue(name, value as Option[]);
      }
    },
    [name, onValue]
  );

  const renderOption = useCallback(
    (option: Option, index: number) => {
      const text = optionsText ? optionsText[index] : option;
      return (
        <MenuItem key={`${name}-item-${index}`} value={String(option)}>
          {checkboxes ? (
            <Checkbox checked={controlledValue ? controlledValue.indexOf(option) > -1 : false} />
          ) : null}
          {text === '' ? <em>{strings.messages.none}</em> : text}
        </MenuItem>
      );
    },
    [checkboxes, controlledValue, name, optionsText, strings.messages.none]
  );

  const renderValue = useCallback(
    (optionValues: Option[]) => {
      const texts = optionValues.map((optionValue: Option) => {
        const index = options.findIndex((option) => option === optionValue);
        return optionsText[index];
      });
      return texts ? texts.join(', ') : undefined;
    },
    [options, optionsText]
  );

  return (
    <FormControl
      variant="outlined"
      sx={
        {
          ...styles.formControl,
          ...props.sx,
        } as SxStyle
      }
      fullWidth={fullWidth}
    >
      <InputLabel id={`${name}-label`}>{label}</InputLabel>
      <Select
        value={controlledValue}
        color="primary"
        label={label}
        renderValue={(value) => renderValue(value as Option[])}
        onChange={(event) => onChangeEvent(event as SelectChangeEvent<Option[]>)}
        error={!hideValidationError && !!errorMessage}
        multiple
        {...props}
      >
        {options.map((option, index) => renderOption(option, index))}
      </Select>
      <FormHelperText error>{!hideValidationError ? errorMessage : null}</FormHelperText>
    </FormControl>
  );
}

export default MultipleSelectWithValidate;
